import { Component, computed, input, output, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { OtpConfig, OtpLogin, RoutingNames, createQR } from 'shared';
import {
  PinInputComponent as oldPinInputComponent,
  TextInputComponent as oldTextInputComponent,
} from '../../form-inputs';
import { AlertComponent as oldAlertComponent } from '../../base/alert/alert.component';
import { ButtonComponent as oldButtonComponent } from '../../base/button/button.component';
import {
  FormGridComponent as oldFormGridComponent,
  FormSectionComponent as oldFormSectionComponent,
} from '../../layouts';
import { HeroComponent, TopBarComponent } from 'design-system-rijks';
import { RouterModule } from '@angular/router';
import {
  NotificationComponent,
  ButtonComponent,
  InputComponent,
  FormGridComponent,
  IconComponent,
  PinComponent,
} from 'design-system';
import { getThemeConfig } from 'shared';

@Component({
  selector: 'lib-otp-form',
  imports: [
    oldAlertComponent,
    oldButtonComponent,
    oldFormGridComponent,
    oldTextInputComponent,
    oldPinInputComponent,
    oldFormSectionComponent,
    RouterModule,
    ButtonComponent,
    InputComponent,
    IconComponent,
    NotificationComponent,
    PinComponent,
    FormGridComponent,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    HeroComponent,
    TopBarComponent,
  ],
  templateUrl: './otp-form.component.html',
  styleUrl: './otp-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OtpFormComponent implements OnInit {
  protected readonly RoutingNames = RoutingNames;

  public loginError = input(false);
  public readyForOtp = input(false);
  public signupUrl = input<string | undefined>();
  public theme = input.required<string>();
  public otpConfig = input.required<OtpConfig | undefined>();

  public sendOtpRequest = output<Partial<OtpLogin>>();
  public loginRequest = output<{ loginUser: OtpLogin; navigateToRoot: boolean }>();
  public signup = output<void>();

  // theming variables
  protected readonly themeConfig = computed(() => getThemeConfig(this.theme()));
  protected readonly logoVariant = computed(() => this.themeConfig().logoVariant);
  protected readonly logoUrl = computed(() => this.themeConfig().logoUrl ?? '');
  protected readonly logoAltText = computed(() => this.themeConfig().logoAltText ?? '');
  protected readonly useRijksVersion = computed(() => this.themeConfig().isRijksTheme);

  protected readonly allowSignup = computed(() => !!this.otpConfig()?.allowSignup);
  protected readonly requirePassword = computed(() => !!this.otpConfig()?.requirePassword);
  protected readonly useAuthenticator = computed(() => !!this.otpConfig()?.useAuthenticator);
  protected readonly contactLink = computed(() => !!this.otpConfig()?.contactLink);
  protected readonly firstTimeLogin = computed(() => !!this.otpConfig()?.firstTimeLogin);

  protected readonly qr = computed(() => {
    const url = this.signupUrl();
    return url ? createQR(url) : undefined;
  });

  protected readonly loginForm: FormGroup;
  protected readonly showFormErrors = signal(false);

  constructor(private fb: FormBuilder) {
    this.loginForm = fb.group({
      emailAddress: ['', Validators.required],
      otp: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    if (this.requirePassword()) {
      this.loginForm.addControl('password', this.fb.control('', Validators.required));
    }
  }

  sendOtp(): void {
    this.sendOtpRequest.emit(this.loginForm.value);
  }

  submit(): void {
    if (this.loginForm.invalid) {
      this.showFormErrors.set(true);
    } else {
      const url = this.signupUrl();
      let loginData = this.loginForm.value;
      if (url) {
        loginData = {
          ...loginData,
          registerQr: true,
        };
      }
      this.loginRequest.emit({
        loginUser: loginData,
        navigateToRoot: true
      });
    }
  }

  signupClick(): void {
    this.signup.emit();
  }
}
